/* eslint-disable react/jsx-props-no-spreading */
import React, {
  DetailedHTMLProps,
  ForwardedRef,
  forwardRef,
  HTMLAttributes,
} from "react";
import {
  Button,
  ButtonProps,
  Modal,
  TextField,
  TextFieldProps,
} from "@warp-ds/react";
import { ModalProps } from "modal/src/props";

type HtmlTag<T> = DetailedHTMLProps<HTMLAttributes<T>, T>;

type Div = HtmlTag<HTMLDivElement>;
type P = HtmlTag<HTMLParagraphElement>;
type H3 = HtmlTag<HTMLHeadingElement>;
type Btn = HtmlTag<HTMLButtonElement>;

export const ItemSeparator = () => <hr />;

export const LastItemSeparator = () => <hr className="-ml-32 -mr-32" />;

export const ModalHeader = ({ children }: Div) => (
  <div className="pb-16 flex flex-col items-center">{children}</div>
);

export const GridLayout = ({ children }: Div) => (
  <div className="py-16 grid grid-cols-8 items-center">{children}</div>
);

export const FooterLayout = ({ children }: Div) => (
  <div className="pt-8 items-center w-full">{children}</div>
);

export const GridLeft = ({ children }: Div) => (
  <div className="col-span-6">{children}</div>
);

export const GridRight = ({ children }: Div) => (
  <div className="col-span-2 flex justify-end">{children}</div>
);

export const Name = ({ children, ...props }: Div) => (
  <div className="text-base font-bold leading-22" {...props}>
    {children}
  </div>
);

export const Description = ({ children }: P) => (
  <p className="text-14 leading-18 font-normal">{children}</p>
);

export const CenteredText = ({ children }: Div) => (
  <div className="text-center">{children}</div>
);

export const SmallButton = ({ children, ...props }: ButtonProps) => (
  <Button className="leading-5 !px-10 py-8" {...props}>
    {children}
  </Button>
);

export const HeaderTitle = forwardRef(
  ({ children }: H3, ref: ForwardedRef<HTMLHeadingElement>) => (
    <h3 ref={ref} className="text-center mt-16 mb-8 break-words w-full">
      {children}
    </h3>
  )
);

export const HeaderBody = ({ children }: Div) => (
  <div className="text-center">{children}</div>
);

export const SettingsModal = ({
  narrow,
  children,
  ...props
}: ModalProps & { narrow: boolean }) => (
  <Modal
    className="saved-search-modal text-16"
    style={
      narrow
        ? {
            // @ts-ignore
            "--w-modal-width": "375px",
          }
        : {
            // @ts-ignore
            "--w-modal-width": "440px",
            "--w-modal-max-height": "90%",
          }
    }
    {...props}
  >
    {children}
  </Modal>
);

export const AbsoluteModalWrapper = ({ children }: Div) => (
  <div className="absolute">{children}</div>
);

export const FooterContainer = ({ children }: Div) => (
  <div className="w-full flex flex-col items-center">{children}</div>
);

export const CancelButton = ({ children, ...props }: ButtonProps) => (
  <Button className="font-bold block p-12 my-8" {...props}>
    {children}
  </Button>
);

export const ConfirmButton = ({ children, ...props }: ButtonProps) => (
  <Button className="font-normal min-w-full block" {...props}>
    {children}
  </Button>
);

export const CloseButtonBody = ({ children, ...props }: Btn) => (
  <button
    type="button"
    className="p-8 rounded-full s-bg-subtle hover:s-bg-subtle-hover active:s-bg-subtle-active s-text-subtle focus-ring"
    style={{
      transform: "translateX(25%)",
    }}
    {...props}
  >
    {children}
  </button>
);

export const CreateButton = ({ children, ...props }: ButtonProps) => (
  <Button className="font-normal min-w-full mt-16" {...props}>
    {children}
  </Button>
);

export const CreateSearchHeader = ({ children }: Div) => (
  <div className="space-y-16 pb-16">{children}</div>
);

export const Spinner = ({ children }: Div) => (
  <div className="animate-spinner">{children}</div>
);

export const LoaderContainer = ({ children }: Div) => (
  <div className="px-28 flex flex-col items-center justify-center gap-10 text-center font-normal">
    {children}
  </div>
);

export const ErrorContainer = ({ children }: Div) => (
  <div className="flex flex-col items-center justify-center gap-16 text-center font-normal">
    {children}
  </div>
);

export const RetryButton = ({ children, ...props }: ButtonProps) => (
  <Button className="font-normal min-w-full w-full" {...props}>
    {children}
  </Button>
);

export const InputContainer = ({ children }: Div) => (
  <div className="text-left">{children}</div>
);

export const StyledTextField = (props: TextFieldProps) => (
  <TextField className="font-normal" {...props} />
);
